import React, { useCallback, useState, useRef, ReactNode } from 'react';
import AGBadge from 'components/AGBadge/AGBadge';
import styles from './SidebarItem.module.css';

export interface SidebarItemProps {
    label: string;
    icon: ReactNode;
    active?: boolean;
    disabled?: boolean;
    isExpanded: boolean;
    onClick: () => void;
    badge?: string;
}

function SidebarItem({ icon, label, active = false, disabled = false, isExpanded, badge, onClick }: SidebarItemProps) {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0 });
    const itemRef = useRef<HTMLDivElement>(null);

    const handleClick = useCallback(() => {
        if (!disabled) onClick();
    }, [disabled, onClick]);

    const handleMouseEnter = useCallback(() => {
        if (!isExpanded && itemRef.current) {
            const rect = itemRef.current.getBoundingClientRect();
            setTooltipPosition({ top: rect.top + rect.height / 2 });
            setShowTooltip(true);
        }
    }, [isExpanded]);

    const handleMouseLeave = useCallback(() => {
        setShowTooltip(false);
    }, []);

    const itemClasses = [
        styles.menuItem,
        active && styles.active,
        disabled && styles.disabled,
        !isExpanded && styles.collapsed,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <li>
            <div
                ref={itemRef}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={itemClasses}
                role="button"
                tabIndex={disabled ? -1 : 0}
            >
                <span className={styles.icon}>{icon}</span>
                <span className={styles.labelWrapper}>
                    <span className={styles.label}>{label}</span>
                    {badge && isExpanded && <AGBadge text={badge} />}
                </span>
                {badge && !isExpanded && <span className={styles.notificationDot} />}
            </div>
            {showTooltip && !isExpanded && (
                <div className={styles.tooltip} style={{ top: tooltipPosition.top, transform: 'translateY(-50%)' }}>
                    {label}
                </div>
            )}
        </li>
    );
}

export default SidebarItem;
